import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { Dispatch, SetStateAction } from "react";
import { DeclarativeNif } from "@/features/flow/types";
import {
  CreateTaxAddressResponse,
  EditTaxAddressResponse,
  isCreateTaxAddressResponseSuccess,
  isUpdateTaxAddressResponseSuccess,
  taxAddressReq
} from "@/features/register/types";
import {
  FullCountry,
  NaturalPerson,
  nifValidationResponse,
  ValidateNifMutationProps
} from "@/features/user/types";
import { COUNTRY_NIF_CHECK_REQUIRED } from "@/features/utils/constants";
import { TaxAddressSchema } from "../../identity-np-tax-address/form-tax-address-identity";

export const handleTaxAddressesCreate = async (
  taxAddresses: TaxAddressSchema[],
  setTaxAddresses: Dispatch<
    SetStateAction<{
      taxAddresses: TaxAddressSchema[];
      isDirty: boolean;
    }>
  >,
  countriesData: { countries: FullCountry[] },
  naturalPerson: NaturalPerson,
  createTaxAddress: UseMutateAsyncFunction<
    CreateTaxAddressResponse | null,
    Error,
    taxAddressReq,
    unknown
  >,
  updateTaxAddress: UseMutateAsyncFunction<
    EditTaxAddressResponse | null,
    Error,
    taxAddressReq,
    unknown
  >,
  validateNif: UseMutateAsyncFunction<
    nifValidationResponse,
    Error,
    ValidateNifMutationProps,
    unknown
  >
): Promise<[boolean, string]> => {
  // Check duplicate countries and convert to declarativeNIF //
  const declarativeNifList: DeclarativeNif[] = [];
  for (const taxAddress of taxAddresses) {
    if (!taxAddress.countryId || !taxAddress.tin) {
      return [false, "flow.id_control.error.must_enter_fields"];
    }
    if (declarativeNifList.find((element) => element.country === taxAddress.countryId)) {
      return [false, "flow.id_control.input.nationality_add_error"];
    }

    declarativeNifList.push({
      UUID: taxAddress.uuid,
      apiCheck: taxAddress.apiCheck,
      country: taxAddress.countryId,
      tin: taxAddress.tin,
      tinCheck: taxAddress.tinCheck,
      tinExist: taxAddress.tinExist
    });
  }

  const taxAddressToUpdate: Promise<EditTaxAddressResponse | null>[] = [];
  for (const taxAddress of declarativeNifList) {
    if (taxAddress.apiCheck) continue;

    const countryIdNumber = Number.parseInt(taxAddress.country!); // Line 48 checked
    const foundCountry = countriesData.countries.find((country) => country.id === countryIdNumber);
    if (!foundCountry) return [false, "flow.id_control.input.nationality_add_error"];

    const countryBirthIsMalta = countriesData.countries.find(
      (item: FullCountry) => item.id === naturalPerson.countryOfBirth && item.alpha2 === "MT"
    );

    if (
      COUNTRY_NIF_CHECK_REQUIRED.includes(foundCountry.alpha2) &&
      (foundCountry.alpha2 !== "MT" || (foundCountry.alpha2 === "MT" && countryBirthIsMalta))
    ) {
      const response = await validateNif({
        nif: taxAddress.tin!,
        countryCode: foundCountry?.alpha2
      });

      if (!response.validSyntax || !response.validStructure) {
        return [false, "olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"];
      }
    }

    taxAddress.apiCheck = true;
    taxAddress.tinCheck = true;
    taxAddress.tinExist = true;
    if (taxAddress.UUID) taxAddressToUpdate.push(updateTaxAddress({ taxAddress: taxAddress }));
  }

  let results: (EditTaxAddressResponse | null)[] | null = null;
  try {
    results = await Promise.all(taxAddressToUpdate);
  } catch (_) {
    return [false, "olkypass.general.error_save"];
  }

  for (const result of results) {
    const updateTaxAddressResponse: EditTaxAddressResponse | null = result;

    if (!updateTaxAddressResponse || !isUpdateTaxAddressResponseSuccess(updateTaxAddressResponse)) {
      return [false, "olkypass.general.error_save"];
    }

    for (const taxAddress of taxAddresses) {
      if (taxAddress.tin === updateTaxAddressResponse.tin) {
        taxAddress.tinCheck = true;
        taxAddress.apiCheck = updateTaxAddressResponse.api_check;
        taxAddress.tinExist = updateTaxAddressResponse.tin_exist;
        break;
      }
    }
  }

  const taxAddressWithoutUuid: DeclarativeNif[] = declarativeNifList.filter(
    (taxAddress) => !taxAddress.UUID
  );

  if (taxAddressWithoutUuid.length === 0) {
    setTaxAddresses({ taxAddresses: taxAddresses, isDirty: false });
    return [true, "olkypass.general.success_save"];
  }

  // CREATION //

  let createTaxAddressResponse: CreateTaxAddressResponse | null = null;
  try {
    createTaxAddressResponse = await createTaxAddress({
      taxAddress: taxAddressWithoutUuid
    });
  } catch (_) {
    return [false, "olkypass.general.error_save"];
  }

  if (!createTaxAddressResponse || !isCreateTaxAddressResponseSuccess(createTaxAddressResponse)) {
    return [false, "olkypass.general.error_save"];
  }

  for (const createdTaxAddress of createTaxAddressResponse) {
    for (const taxAddress of taxAddresses) {
      if (taxAddress.tin === createdTaxAddress.tin) {
        taxAddress.uuid = createdTaxAddress.uuid;
        taxAddress.tinCheck = true;
        taxAddress.apiCheck = true;
        taxAddress.tinExist = true;
        break;
      }
    }
  }

  setTaxAddresses({ taxAddresses: taxAddresses, isDirty: false });
  return [true, "olkypass.general.success_save"];
};
